export type EmbedEvent =
  | 'sessionStart' | 'sessionEnd' | 'agentStateChange'
  /**
   * The agent worker has actually joined the room. NOT the same as
   * `sessionStart`, which fires the moment the LiveKit room connects — the
   * agent joins seconds later (cold start builds STT/LLM/TTS/VAD first).
   * Every push API (`tellAgent`/`informAgent`/…) fails with
   * 'no agent connected' in that window, so a host bridge script that fires
   * on load or on `sessionStart` silently loses its call. Wait for this.
   * Fires at most once per session, and fires immediately-on-subscribe
   * semantics are NOT provided — subscribe before calling `connect()`.
   */
  | 'agentReady'
  | 'avatarReady'
  | 'avatarMediaReady'
  | 'transcript' | 'toolCall' | 'citation' | 'error'
  /**
   * A scene arrived on the `ui-engine:scene` data-channel topic. The payload
   * is the publisher's envelope, forwarded verbatim:
   *
   *   `{ type: 'skeleton', layout }`  — layout-only placeholder, published
   *      microseconds before the real thing so the surface can reserve space.
   *   `{ type: 'scene', dsl }`        — the pipe-delimited DSL (ONE_BRAIN).
   *   `{ type: 'scene', ...fields }`  — legacy JSON scene fields, still
   *      possible from TWO_BRAIN's `ui-engine/livekit_publisher.py`.
   *
   * Fires once per publish, so several times per conversational turn — the
   * scene_viewer prompt tells the agent to draw on EVERY response.
   *
   * Note this event predates the renderer: it was declared here and never
   * emitted while `room.ts` filtered the topic out.
   */
  | 'scene';

export interface EventBus {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on<T = any>(event: EmbedEvent, handler: (payload: T) => void): () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  off(event: EmbedEvent, handler: (payload: any) => void): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emit(event: EmbedEvent, payload: any): void;
}

export function createEventBus(): EventBus {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const map = new Map<EmbedEvent, Set<(payload: any) => void>>();
  return {
    on(event, handler) {
      let set = map.get(event);
      if (!set) { set = new Set(); map.set(event, set); }
      set.add(handler);
      return () => set!.delete(handler);
    },
    off(event, handler) {
      map.get(event)?.delete(handler);
    },
    emit(event, payload) {
      const set = map.get(event);
      if (set) {
        for (const h of set) {
          try { h(payload); } catch (e) { console.error('[Mobeus] event handler threw:', e); }
        }
      }
      // CustomEvent shim: every bus event also fires as a `mobeus:<event>`
      // window event with `payload` as `detail`, so hosts can listen via
      // `window.addEventListener('mobeus:transcript', ...)` if they prefer
      // DOM events over `Mobeus.on(...)`. Gated on `typeof window` for SSR
      // and node-env vitest files.
      if (typeof window !== 'undefined') {
        try {
          window.dispatchEvent(new CustomEvent('mobeus:' + event, { detail: payload }));
        } catch (e) {
          console.warn('[Mobeus] failed to dispatch window event mobeus:' + event, e);
        }
      }
    },
  };
}
